import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconShieldCheck, IconLock, IconHeadset } from '@tabler/icons-react';
import { useWhitelabelBranding } from '../../hooks/useWhitelabel';
import {
  UsageTypeSelector,
  PlanSelector,
  FreeRegistrationForm,
  PaymentStepForm,
  PaidRegisterForm,
  OnboardingQuestions,
} from './components';
import './css/signup.css';
import '../Checkout/css/checkout.css';

const STEPS = {
  USAGE_TYPE: 'usage_type',
  PLAN_SELECT: 'plan_select',
  REGISTER: 'register',
  PAYMENT: 'payment',
  REGISTER_PAID: 'register_paid',
  ONBOARDING: 'onboarding',
};

// Labels da barra de progresso, dependendo do modo
const STEP_LABELS_FREE = ['Tipo de uso', 'Plano', 'Cadastro', 'Sobre você'];
const STEP_LABELS_PAID = ['Tipo de uso', 'Plano', 'Pagamento', 'Cadastro', 'Sobre você'];

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isWhitelabel } = useWhitelabelBranding();
  const [signupMode, setSignupMode] = useState('free'); // 'free' | 'paid'
  const [currentStep, setCurrentStep] = useState(STEPS.USAGE_TYPE);
  const [usageType, setUsageType] = useState(null);
  const [selectedPlanConfig, setSelectedPlanConfig] = useState(null);
  const [planFilter, setPlanFilter] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [registerResult, setRegisterResult] = useState(null);
  const [animating, setAnimating] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isWhitelabel) {
      navigate('/login', { replace: true });
    }
  }, [isWhitelabel, navigate]);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    const planParam = params.get('plan');
    const modeParam = params.get('mode');

    if (modeParam === 'paid') {
      setSignupMode('paid');
    }

    if (typeParam === 'personal') {
      setUsageType('personal');
      setPlanFilter(planParam || null);
      setCurrentStep(STEPS.PLAN_SELECT);
    } else if (typeParam === 'commercial') {
      setUsageType('commercial');
      setPlanFilter(planParam || null);
      setCurrentStep(STEPS.PLAN_SELECT);
    }
  }, [location.search]);

  const animateTransition = useCallback((callback) => {
    setAnimating(true);
    setTimeout(() => {
      callback();
      setTimeout(() => setAnimating(false), 50);
    }, 250);
  }, []);

  const handleUsageSelect = useCallback((type) => {
    animateTransition(() => {
      setUsageType(type);
      setCurrentStep(STEPS.PLAN_SELECT);
    });

    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_category: type === 'personal' ? 'personal' : 'commercial',
      });
    }
  }, [animateTransition]);

  const handleSelectPlan = useCallback((planConfig) => {
    animateTransition(() => {
      setSelectedPlanConfig(planConfig);
      // Em modo paid, próximo step é PAYMENT (não REGISTER)
      setCurrentStep(signupMode === 'paid' ? STEPS.PAYMENT : STEPS.REGISTER);
    });
  }, [animateTransition, signupMode]);

  const handlePaymentSuccess = useCallback((data) => {
    animateTransition(() => {
      setPaymentResult(data);
      setCurrentStep(STEPS.REGISTER_PAID);
    });
  }, [animateTransition]);

  const handleRegisterSuccess = useCallback((data) => {
    animateTransition(() => {
      setRegisterResult(data);
      setCurrentStep(STEPS.ONBOARDING);
    });
  }, [animateTransition]);

  const handleOnboardingFinish = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const handleBack = useCallback(() => {
    animateTransition(() => {
      if (currentStep === STEPS.REGISTER) {
        setSelectedPlanConfig(null);
        setCurrentStep(STEPS.PLAN_SELECT);
      } else if (currentStep === STEPS.PAYMENT) {
        setSelectedPlanConfig(null);
        setCurrentStep(STEPS.PLAN_SELECT);
      } else if (currentStep === STEPS.PLAN_SELECT) {
        setUsageType(null);
        setPlanFilter(null);
        setCurrentStep(STEPS.USAGE_TYPE);
      }
      // Voltar não é permitido em REGISTER_PAID — pagamento já foi cobrado;
      // usuário tem que finalizar para vincular ao Account.
    });
  }, [currentStep, animateTransition]);

  const stepLabels = signupMode === 'paid' ? STEP_LABELS_PAID : STEP_LABELS_FREE;

  const getStepIndex = () => {
    if (signupMode === 'paid') {
      switch (currentStep) {
        case STEPS.USAGE_TYPE: return 0;
        case STEPS.PLAN_SELECT: return 1;
        case STEPS.PAYMENT: return 2;
        case STEPS.REGISTER_PAID: return 3;
        case STEPS.ONBOARDING: return 4;
        default: return 0;
      }
    }
    switch (currentStep) {
      case STEPS.USAGE_TYPE: return 0;
      case STEPS.PLAN_SELECT: return 1;
      case STEPS.REGISTER: return 2;
      case STEPS.ONBOARDING: return 3;
      default: return 0;
    }
  };

  const getHeaderText = () => {
    const isPaid = signupMode === 'paid';
    switch (currentStep) {
      case STEPS.USAGE_TYPE:
        return {
          title: 'Como você pretende usar a plataforma?',
          subtitle: 'Escolha a opção que melhor se encaixa no seu momento',
        };
      case STEPS.PLAN_SELECT:
        return {
          title: usageType === 'personal' ? 'Planos para uso pessoal' : 'Planos para uso comercial',
          subtitle: isPaid
            ? 'Escolha seu plano para contratar agora'
            : 'Todos os planos com teste grátis de 7 dias, sem cartão de crédito',
        };
      case STEPS.REGISTER:
        return {
          title: 'Teste grátis por 7 dias!',
          subtitle: `Preencha seus dados para começar a usar o plano ${selectedPlanConfig?.planName || ''}`,
        };
      case STEPS.PAYMENT:
        return {
          title: 'Contratação imediata',
          subtitle: `Confirme o pagamento para ativar o plano ${selectedPlanConfig?.planName || ''}`,
        };
      case STEPS.REGISTER_PAID:
        return {
          title: 'Quase lá!',
          subtitle: 'Pagamento confirmado — finalize seu cadastro para liberar o acesso',
        };
      case STEPS.ONBOARDING:
        return {
          title: 'Conte um pouco sobre você',
          subtitle: 'Suas respostas nos ajudam a personalizar sua jornada — leva menos de 1 minuto.',
        };
      default:
        return { title: '', subtitle: '' };
    }
  };

  const stepIndex = getStepIndex();
  const header = getHeaderText();

  // Voltar é permitido em todas as etapas exceto USAGE_TYPE, ONBOARDING e REGISTER_PAID
  const showBackButton =
    currentStep !== STEPS.USAGE_TYPE &&
    currentStep !== STEPS.ONBOARDING &&
    currentStep !== STEPS.REGISTER_PAID;

  return (
    <div className="modern-login-container">
      {/* Background */}
      <div className="login-background">
        <div className="gradient-overlay"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="signup-content">
        {/* Progress Steps */}
        <div className="signup-progress">
          {stepLabels.map((label, i) => (
            <div
              key={i}
              className={`signup-progress__step ${
                i < stepIndex ? 'signup-progress__step--done' : ''
              } ${i === stepIndex ? 'signup-progress__step--active' : ''}`}
            >
              <div className="signup-progress__dot">
                {i < stepIndex ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <span className="signup-progress__label">{label}</span>
              {i < stepLabels.length - 1 && <div className="signup-progress__line" />}
            </div>
          ))}
        </div>

        {/* Header */}
        <div className={`signup-header ${animating ? 'signup-header--exit' : 'signup-header--enter'}`}>
          <h1 className="signup-header__title">{header.title}</h1>
          <p className="signup-header__subtitle">{header.subtitle}</p>
        </div>

        {/* Step Content */}
        <div
          ref={contentRef}
          className={`signup-step-content ${animating ? 'signup-step-content--exit' : 'signup-step-content--enter'}`}
        >
          {currentStep === STEPS.USAGE_TYPE && (
            <UsageTypeSelector onSelect={handleUsageSelect} />
          )}

          {currentStep === STEPS.PLAN_SELECT && (
            <PlanSelector
              usageType={usageType}
              onSelectPlan={handleSelectPlan}
              planFilter={planFilter}
              mode={signupMode}
            />
          )}

          {currentStep === STEPS.REGISTER && selectedPlanConfig && (
            <FreeRegistrationForm
              planConfig={selectedPlanConfig}
              onRegisterSuccess={handleRegisterSuccess}
            />
          )}

          {currentStep === STEPS.PAYMENT && selectedPlanConfig && (
            <PaymentStepForm
              planConfig={selectedPlanConfig}
              onPaymentSuccess={handlePaymentSuccess}
            />
          )}

          {currentStep === STEPS.REGISTER_PAID && selectedPlanConfig && paymentResult && (
            <PaidRegisterForm
              planConfig={selectedPlanConfig}
              paymentResult={paymentResult}
              onRegisterSuccess={handleRegisterSuccess}
            />
          )}

          {currentStep === STEPS.ONBOARDING && registerResult && (
            <OnboardingQuestions
              accountId={registerResult.accountId}
              signupToken={registerResult.signupToken}
              userEmail={registerResult.userEmail}
              trialDays={registerResult.trialDays}
              onFinish={handleOnboardingFinish}
            />
          )}
        </div>

        {/* Navigation - Voltar */}
        {showBackButton && (
          <div className="signup-nav">
            <button className="signup-nav__back" onClick={handleBack}>
              <IconArrowLeft size={18} />
              Voltar
            </button>
          </div>
        )}

        {/* Trust Badges */}
        <div className="signup-trust">
          <div className="signup-trust__item">
            <IconShieldCheck size={16} />
            <span>Dados protegidos</span>
          </div>
          <div className="signup-trust__item">
            <IconLock size={16} />
            <span>Conexão segura</span>
          </div>
          <div className="signup-trust__item">
            <IconHeadset size={16} />
            <span>Suporte humanizado</span>
          </div>
        </div>

        {/* Login link */}
        <div className="signup-login-link">
          Já tem uma conta?{' '}
          <a href="/login">Fazer login</a>
        </div>
      </div>
    </div>
  );
};

export default Signup;
